import { useState, useEffect } from 'react';
import { Avatar, AvatarImage, AvatarFallback } from './avatar';
import { Button } from './button';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, X } from 'lucide-react';
import { db, DefaultAvatar } from '../../lib/database';
import { useToast } from './use-toast';

interface AvatarSelectorProps {
  currentAvatar?: string;
  onSelect: (avatarUrl: string) => Promise<void>;
  onClose: () => void;
  isOpen: boolean;
}

export const AvatarSelector = ({ 
  currentAvatar, 
  onSelect, 
  onClose, 
  isOpen 
}: AvatarSelectorProps) => {
  const [defaultAvatars, setDefaultAvatars] = useState<DefaultAvatar[]>([]);
  const [selectedAvatar, setSelectedAvatar] = useState(currentAvatar || '');
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (isOpen) {
      loadDefaultAvatars();
    }
  }, [isOpen]);

  const loadDefaultAvatars = async () => {
    setLoading(true);
    try {
      const avatars = await db.getDefaultAvatars();
      setDefaultAvatars(avatars);
    } catch (error) {
      console.error('Error loading default avatars:', error);
      toast({
        title: "Error",
        description: "Failed to load avatar options. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAvatar = (avatarUrl: string) => {
    setSelectedAvatar(avatarUrl);
  };

  const handleConfirm = async () => {
    if (!selectedAvatar) return;

    setIsSubmitting(true);
    try {
      await onSelect(selectedAvatar);
      onClose();
    } catch (error) {
      console.error('Error updating avatar:', error);
      toast({
        title: "Error",
        description: "Failed to update avatar. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full max-h-[80vh] overflow-hidden"
        >
          {/* Header */}
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Choose Profile Picture
              </h2>
              <Button 
                variant="ghost" 
                size="icon"
                onClick={onClose}
                className="h-8 w-8"
              >
                <X size={16} />
              </Button>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Select one of our default avatars for your profile
            </p>
          </div>

          {/* Avatar Grid */}
          <div className="p-6 max-h-96 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : (
              <div className="grid grid-cols-4 gap-3">
                {defaultAvatars.map((avatar) => (
                  <motion.button
                    key={avatar.id}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleSelectAvatar(avatar.url)}
                    className={`relative aspect-square bg-gray-100 dark:bg-gray-700 rounded-lg border-2 transition-all flex items-center justify-center ${
                      selectedAvatar === avatar.url
                        ? 'border-primary bg-primary/5'
                        : 'border-gray-200 dark:border-gray-600 hover:border-primary/50'
                    }`}
                  >
                    <img 
                      src={avatar.url} 
                      alt={avatar.name}
                      className="w-16 h-16 object-contain"
                    />
                    
                    {selectedAvatar === avatar.url && (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="absolute top-1 right-1 bg-primary text-white rounded-full p-1"
                      >
                        <Check size={14} />
                      </motion.div>
                    )}
                  </motion.button>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
            <Button 
              variant="outline" 
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleConfirm}
              disabled={!selectedAvatar || isSubmitting || selectedAvatar === currentAvatar}
            >
              {isSubmitting ? (
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Updating...</span>
                </div>
              ) : (
                'Update Avatar'
              )}
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}; 